/**
 * Tab definitions for the cost-model configuration screen.
 *
 * One tab per module (Material Traceability, PipeFitter, NDT, Coating) plus the
 * shared NRE onboarding, two-part platform fee, retention, and bolt-on hardware
 * tabs. Field keys mirror the backend module_configs schemas. Fraction inputs
 * (margins, capture/extract shares, overheads) are plain decimals in [0, 1],
 * flagged in the label, so they are not rendered with a percent affix.
 */

import { ModuleConfigTabDef } from './moduleConfigTypes';

const DOLLAR = '$';
const RATE_ICON =
  'M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z';

export const MODULE_CONFIG_TABS: ModuleConfigTabDef[] = [
  {
    id: 'material',
    label: 'Material Traceability',
    icon: RATE_ICON,
    configKey: 'material_config',
    description:
      'Material Traceability cost model: the per-pipe cost build-up (a margin target derives the price), the secondary-material add-on fees, the default service tier, and the module floor.',
    fields: [
      { key: 'reportsPerPipe', label: 'Mill Reports Per Pipe' },
      { key: 'autoExtractRate', label: 'Auto-Extract Rate (0-1)' },
      { key: 'minutesPerException', label: 'Minutes Per Exception', suffix: 'min' },
      { key: 'hourlyRate', label: 'Exception Labour Hourly Rate', symbol: DOLLAR },
      { key: 'computeCostPerDoc', label: 'Extraction Compute Per Doc', symbol: DOLLAR },
      { key: 'storagePerPipe', label: 'Storage Cost Per Pipe', symbol: DOLLAR },
      { key: 'qaOverheadPct', label: 'QA Overhead (0-1)' },
      { key: 'marginTarget', label: 'Margin Target (0-1)' },
      { key: 'floorPerPipe', label: 'Floor Per Pipe', symbol: DOLLAR },
      { key: 'ceilingPerPipe', label: 'Ceiling Per Pipe (0 = none)', symbol: DOLLAR },
      { key: 'gasAddOnFee', label: 'Gas Tracking Add-On', symbol: DOLLAR },
      { key: 'wireAddOnFee', label: 'Wire Tracking Add-On', symbol: DOLLAR },
      {
        key: 'coatingMaterialsAddOnFee',
        label: 'Coating-Materials Tracking Add-On',
        symbol: DOLLAR,
      },
      {
        key: 'defaultServiceTier',
        label: 'Default Service Tier',
        type: 'select',
        options: [
          { value: 'extended', label: 'Extended' },
          { value: 'storage_only', label: 'Storage only' },
        ],
      },
      { key: 'moduleFloor', label: 'Module Floor', symbol: DOLLAR },
    ],
    hasComputeMultipliers: true,
  },
  {
    id: 'pipefitter',
    label: 'PipeFitter',
    icon: RATE_ICON,
    configKey: 'pipefitter_config',
    description:
      'PipeFitter cost inputs: the CRC scanning service, the per-joint reporting rates, the algorithm per-joint rates and gain-share bounds, and the module floor.',
    fields: [
      { key: 'technicianDayRate', label: 'Scanning Technician Day-Rate', symbol: DOLLAR },
      { key: 'scannerDayCost', label: 'Scanner Day Cost', symbol: DOLLAR },
      { key: 'pipesPerDay', label: 'Scanning Throughput', suffix: '/day' },
      { key: 'scanMargin', label: 'Scanning Service Margin (0-1)' },
      { key: 'mobilization', label: 'Scanning Mobilization', symbol: DOLLAR },
      { key: 'pipeDataPerJoint', label: 'Pipe Data Report Per Joint', symbol: DOLLAR },
      { key: 'pipeCompatibilityPerJoint', label: 'Pipe Compatibility Report Per Joint', symbol: DOLLAR },
      { key: 'optiFitPerJoint', label: 'OptiFit Per Joint', symbol: DOLLAR },
      { key: 'omniFitPerJoint', label: 'OmniFit Per Joint', symbol: DOLLAR },
      { key: 'captureShare', label: 'Gain-Share Capture (0-1)' },
      { key: 'gainShareFloor', label: 'Gain-Share Floor Per Joint', symbol: DOLLAR },
      { key: 'optiFitCap', label: 'OptiFit Cap Per Joint', symbol: DOLLAR },
      { key: 'omniFitCap', label: 'OmniFit Cap Per Joint', symbol: DOLLAR },
      { key: 'moduleFloor', label: 'Module Floor', symbol: DOLLAR },
    ],
  },
  {
    id: 'ndt',
    label: 'NDT',
    icon: RATE_ICON,
    configKey: 'ndt_config',
    description:
      'NDT per-inspection cost model (shared with Material Traceability): per-document extraction compute + manual exception labour (the share that fails auto-extraction) + QA overhead + storage, multiplied by reports per inspection and divided by (1 - margin). The NDT + AI tier adds an AI compute cost per document.',
    fields: [
      { key: 'reportsPerInspection', label: 'Reports Per Inspection' },
      { key: 'autoExtractRate', label: 'Auto-Extract Rate (0-1)' },
      { key: 'minutesPerException', label: 'Minutes Per Exception', suffix: 'min' },
      { key: 'hourlyRate', label: 'Exception Labour Hourly Rate', symbol: DOLLAR },
      { key: 'computeCostPerDoc', label: 'Extraction Compute Per Doc', symbol: DOLLAR },
      { key: 'aiComputeCostPerDoc', label: 'AI Compute Per Doc (AI tier)', symbol: DOLLAR },
      { key: 'storageCostPerInspection', label: 'Storage Cost Per Inspection', symbol: DOLLAR },
      { key: 'qaOverheadPct', label: 'QA Overhead (0-1)' },
      { key: 'marginTarget', label: 'Margin Target (0-1)' },
      { key: 'floorPerInspection', label: 'Floor Per Inspection', symbol: DOLLAR },
      { key: 'ceilingPerInspection', label: 'Ceiling Per Inspection (0 = none)', symbol: DOLLAR },
      { key: 'moduleFloor', label: 'Module Floor', symbol: DOLLAR },
    ],
    hasComputeMultipliers: true,
  },
  {
    id: 'coating',
    label: 'Coating',
    icon: RATE_ICON,
    configKey: 'coating_config',
    description:
      'Coating per-joint record cost model (shared with Material Traceability): per-document extraction compute + manual exception labour + QA overhead + storage, multiplied by reports per joint and divided by (1 - margin). Bolt-on contractors reuse the shared welding config.',
    fields: [
      { key: 'reportsPerJoint', label: 'Reports Per Joint' },
      { key: 'autoExtractRate', label: 'Auto-Extract Rate (0-1)' },
      { key: 'minutesPerException', label: 'Minutes Per Exception', suffix: 'min' },
      { key: 'hourlyRate', label: 'Exception Labour Hourly Rate', symbol: DOLLAR },
      { key: 'computeCostPerDoc', label: 'Extraction Compute Per Doc', symbol: DOLLAR },
      { key: 'storageCostPerJoint', label: 'Storage Cost Per Joint', symbol: DOLLAR },
      { key: 'qaOverheadPct', label: 'QA Overhead (0-1)' },
      { key: 'marginTarget', label: 'Margin Target (0-1)' },
      { key: 'floorPerJoint', label: 'Floor Per Joint', symbol: DOLLAR },
      { key: 'ceilingPerJoint', label: 'Ceiling Per Joint (0 = none)', symbol: DOLLAR },
      { key: 'moduleFloor', label: 'Module Floor', symbol: DOLLAR },
    ],
    hasComputeMultipliers: true,
  },
  {
    id: 'nre',
    label: 'NRE Onboarding',
    icon: RATE_ICON,
    configKey: 'nre_config',
    description:
      'Shared onboarding (non-recurring engineering) cost model: fixed setup hours, per-unit hours, a validation overhead, the engineer rate, the margin target, and a difficulty multiplier per source arrival type.',
    fields: [
      { key: 'setupHours', label: 'Setup Hours Per Source', suffix: 'hrs' },
      { key: 'perUnitHours', label: 'Hours Per Data Type', suffix: 'hrs' },
      { key: 'validationOverhead', label: 'Validation Overhead (0-1)' },
      { key: 'engineerRate', label: 'Engineer Hourly Rate', symbol: DOLLAR },
      { key: 'marginTarget', label: 'Margin Target (0-1)' },
    ],
    hasDifficultyMultipliers: true,
  },
  {
    id: 'platform',
    label: 'Platform Fee',
    icon: RATE_ICON,
    configKey: 'platform_config',
    description:
      'Shared platform fee cost inputs: infra (shared baseline plus a per-module increment that scales with the number of selected modules) plus support hours times the hourly rate, a per-user monthly fee, at the platform margin.',
    fields: [
      {
        key: 'baseInfraMonthly',
        label: 'Base Infra Monthly',
        symbol: DOLLAR,
        tooltip:
          'Shared baseline infra cost that exists for any deal (database, app ' +
          'servers, load balancer, networking, monitoring, backups).',
      },
      {
        key: 'perModuleInfraMonthly',
        label: 'Per-Module Infra Monthly',
        symbol: DOLLAR,
        tooltip:
          'Infra increment added per selected module. Total infra = baseline + ' +
          'this x module count. Calibrated so 5 modules lands near $800/mo.',
      },
      { key: 'baseSupportHours', label: 'Base Support Hours', suffix: 'hrs' },
      { key: 'supportHourlyRate', label: 'Support Hourly Rate', symbol: DOLLAR },
      {
        key: 'perUserMonthly',
        label: 'Per-User Monthly',
        symbol: DOLLAR,
        tooltip:
          'Platform fee charged per user per month. Multiplied by the number ' +
          'of users on the proposal and added to the platform monthly total, ' +
          'regardless of who hosts the infra.',
      },
      { key: 'marginTarget', label: 'Margin Target (0-1)' },
    ],
  },
  {
    id: 'retention',
    label: 'Retention',
    icon: RATE_ICON,
    configKey: 'retention_config',
    description:
      'Shared post-handover retention annual fees. Cold archive keeps the compliance dataset in immutable storage; warm access keeps the application running.',
    fields: [
      { key: 'coldArchiveAnnualFee', label: 'Cold-Archive Annual Fee', symbol: DOLLAR },
      { key: 'warmAccessAnnualFee', label: 'Warm-Access Annual Fee', symbol: DOLLAR },
    ],
  },
  {
    id: 'boltonHardware',
    label: 'Bolt-On Hardware',
    icon: RATE_ICON,
    configKey: 'bolton_hardware_config',
    description:
      'Shared bolt-on hardware rates (capture path B). Priced exactly like welding hardware: unit cost, expected utilization, profit margin, and monthly rental rate.',
    fields: [
      { key: 'unitCost', label: 'Unit Cost', symbol: DOLLAR },
      { key: 'utilization', label: 'Expected Utilization', isPercentage: true },
      { key: 'profitMargin', label: 'Profit Margin', isPercentage: true },
      { key: 'rentalRate', label: 'Monthly Rental Rate', isPercentage: true },
    ],
  },
];
